import { useEffect, useCallback, useRef } from 'react';

const tg = typeof window !== 'undefined' ? window.Telegram?.WebApp : undefined;

interface MainButtonOptions {
  text: string;
  onClick: () => void;
  visible?: boolean;
  disabled?: boolean;
  color?: string;
  textColor?: string;
  loading?: boolean;
}

export function useMainButton({
  text,
  onClick,
  visible = true,
  disabled = false,
  color,
  textColor,
  loading = false,
}: MainButtonOptions) {
  const callbackRef = useRef(onClick);
  callbackRef.current = onClick;

  const stableCallback = useCallback(() => {
    callbackRef.current();
  }, []);

  useEffect(() => {
    if (!tg?.MainButton) return;

    const hasValidText = typeof text === 'string' && text.trim() !== '';
    // Telegram WebApp throws WebAppBottomButtonParamInvalid if setText('') or setText(' ')
    if (!hasValidText) {
      tg.MainButton.offClick(stableCallback);
      tg.MainButton.hide();
      return () => tg.MainButton.offClick(stableCallback);
    }

    tg.MainButton.setText(text);

    tg.MainButton.color = color ?? '#7c3aed';
    tg.MainButton.textColor = textColor ?? '#ffffff';

    if (visible) {
      tg.MainButton.show();
    } else {
      tg.MainButton.hide();
    }

    if (disabled) {
      tg.MainButton.disable();
    } else {
      tg.MainButton.enable();
    }

    if (loading) {
      tg.MainButton.showProgress(false);
    } else {
      tg.MainButton.hideProgress();
    }

    tg.MainButton.onClick(stableCallback);

    return () => {
      tg.MainButton.offClick(stableCallback);
    };
  }, [text, visible, disabled, color, textColor, loading, stableCallback]);

  useEffect(() => {
    return () => {
      if (!tg?.MainButton) return;
      tg.MainButton.hide();
      tg.MainButton.offClick(stableCallback);
      tg.MainButton.hideProgress();
    };
  }, [stableCallback]);
}
